import { api } from "../../../../api"
import { setAlertFunc } from "../../../../components/AlertToaster/AlertSlice"
import { DropdownObj, IFile, IResponse } from "../../../Common/commonTypes"
import { getDemolition, getDemolitionTabs } from "../../OperationSlice"
import { getButtonDropdown, getTicketDetails } from "./DemolitionSlice"

const MODULE_URL = "operations/demolitions"

export type TDemolitionChargerDetails = {
    charger_id: number
    charger_name: string
    charger_sku: string
    charger_type: string | null
    cin: string | null
    decommissioning_date: string | null
    description: string | null
    is_demolished: boolean
    oem: string | null
    status: string | null
    sub_status: string | null
}

const demolitionService = api.injectEndpoints({
    endpoints: build => ({
        getDemolitionTicketDetails: build.query<any, string>({
            query: ticket_id => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "GET",
                params: { ticket_id },
            }),
            providesTags: ["Demolition", "Survey"],
        }),
        getDemolitionChargerDetails: build.query<TDemolitionChargerDetails[], string>({
            query: ticket_id => ({
                url: `${MODULE_URL}/charger_details`,
                method: "GET",
                params: { ticket_id },
            }),
            providesTags: ["Demolition", "Survey"],
        }),
        getDemolitionDRCreationStatus: build.query<
            any,
            {
                ticket_id: string
                project_id: string
                destination_site_id: string
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/check_demo_dr_creation_status`,
                method: "GET",
                params,
            }),
        }),
        addDemolitionDR: build.mutation<any, FormData>({
            query: data => ({
                url: `${MODULE_URL}/delivery_request`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data,
                        })
                    )
                    dispatch(getTicketDetails(arg.get("ticket_id")))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        addReverseDemolitionDR: build.mutation<any, FormData>({
            query: data => ({
                url: `${MODULE_URL}/reverse_logistics`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.msg }))
                    dispatch(getTicketDetails(arg.get("ticket_id")))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        addDemolitionTicket: build.mutation<
            {
                add_payment_terms: boolean
                finance_id: number
                msg: string
            },
            FormData
        >({
            query: data => ({
                url: `${MODULE_URL}/ticket`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
                try {
                    const res = await queryFulfilled
                    const operationState = (getState() as any).operation
                    const demolitionFormValue = operationState.DemolitionFormValue
                    const selectedValue = operationState.selectedValue
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                    dispatch(getDemolition({ data: demolitionFormValue, page: 1, selectedValue: selectedValue }))
                    dispatch(getDemolitionTabs({ data: demolitionFormValue, page: 1, selectedValue: selectedValue }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition"],
        }),
        editDemolitionTicket: build.mutation<any, FormData>({
            query: data => ({
                url: `${MODULE_URL}/ticket`,
                method: "PUT",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.msg }))
                    dispatch(getButtonDropdown(arg.get("ticket_id")))
                    dispatch(getTicketDetails(arg.get("ticket_id")))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        editDemolitionChargerDetails: build.mutation<
            { message: string },
            {
                ticket_id: string
                project_id: string
                added: any[]
                edited: any[]
                deleted: { charger_id: number }[]
            }
        >({
            query: data => ({
                url: `${MODULE_URL}/charger_details`,
                method: "PUT",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        requestDemolitionSurvey: build.mutation<IResponse<[string]>, FormData>({
            query: data => ({
                url: `${MODULE_URL}/request_survey`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({ type: "success", msg: data?.data?.[0] || "Site survey requested successfully!" })
                    )
                    dispatch(getButtonDropdown(arg.get("ticket_id")))
                    dispatch(getTicketDetails(arg.get("ticket_id")))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        linkDemolitionSurvey: build.mutation<IResponse<string>, FormData>({
            query: data => ({
                url: `${MODULE_URL}/link_survey`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data || "Site survey linked successfully!" }))
                    dispatch(getButtonDropdown(arg.get("ticket_id")))
                    dispatch(getTicketDetails(arg.get("ticket_id")))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        sendDemolitionAlert: build.mutation<
            {
                message: string
            },
            {
                ticket_id: number
            }
        >({
            query: data => ({
                url: `${MODULE_URL}/send_demolition_alert`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.message || "Alert sent successfully!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),
        addDemolitionDrV2: build.mutation<string, FormData>({
            query: data => ({
                url: `${MODULE_URL}/delivery_request_v2`,
                method: "POST",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(getTicketDetails(arg.get("ticket_id")))
                    dispatch(setAlertFunc({ type: "success", msg: data || "Delivery request added successfully!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Demolition", "Survey"],
        }),
        downloadDemolitionTickets: build.query<IFile, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download_demolition_tickets`,
                params: {
                    state_id: JSON.stringify(params.data.stateId ?? []),
                    status_ids: JSON.stringify(params.data.statusId ?? []),
                    city_id: JSON.stringify(params.data.cityId ?? []),
                    deal_done_by_id: JSON.stringify(params.data.deal_done_by_id ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    reason_ids: JSON.stringify(params.data.reasons ?? []),
                    site_ids: params?.data?.site_ids?.length
                        ? JSON.stringify(params.data.site_ids.map((i: DropdownObj) => i.id))
                        : "[]",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    updated_on_start: params.data.UpdateStartDate ?? "",
                    updated_on_end: params.data.UpdateEndDate ?? "",
                    vendor_ids: JSON.stringify(params.data.vendor_ids ?? []),
                    search_key: params.data.search_key ?? "",
                    raised_by: JSON.stringify(params.data.raised_by_id ?? []),
                    read_filter: params.data.ReadUnread ?? "all",
                    queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                    parent_status: params.data.parent_status ?? "all_tab",
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (typeof data === "string" || data?.urls?.download) {
                        const message = typeof data === "string" ? data : "Downloaded successfully"
                        dispatch(
                            setAlertFunc({
                                type: "success",
                                msg: message,
                            })
                        )
                    }
                } catch (error: any) {}
            },
            keepUnusedDataFor: 0,
        }),
    }),
})

export const {
    useGetDemolitionTicketDetailsQuery,
    useGetDemolitionChargerDetailsQuery,
    useAddDemolitionTicketMutation,
    useAddDemolitionDRMutation,
    useEditDemolitionTicketMutation,
    useEditDemolitionChargerDetailsMutation,
    useRequestDemolitionSurveyMutation,
    useLinkDemolitionSurveyMutation,
    useSendDemolitionAlertMutation,
    useAddDemolitionDrV2Mutation,
    useLazyGetDemolitionDRCreationStatusQuery,
    useAddReverseDemolitionDRMutation,
    useLazyDownloadDemolitionTicketsQuery,
} = demolitionService
